import React from 'react';
import { Toaster, toast as hotToast, ToastOptions } from 'react-hot-toast';
import { ApiError } from '../../utils/retryUtils';
import i18n from '../../i18n';

// Configuration personnalisée pour les toasts
export const toastConfig = {
  duration: 4000,
  position: 'top-right' as const,
  style: {
    borderRadius: '16px',
    background: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    color: '#1e293b',
    fontSize: '14px',
    fontWeight: '600',
    boxShadow: '0 20px 25px -5px rgba(0, 50, 115, 0.1), 0 10px 10px -5px rgba(0, 50, 115, 0.04)',
    border: 'none',
  },
  success: {
    iconTheme: {
      primary: '#10b981',
      secondary: '#fff',
    },
  },
  error: {
    iconTheme: {
      primary: '#ef4444',
      secondary: '#fff',
    },
  },
  loading: {
    iconTheme: {
      primary: '#3b82f6',
      secondary: '#fff',
    },
  },
};

// Composant Toaster personnalisé
export const CustomToaster: React.FC = () => {
  return (
    <Toaster
      position={toastConfig.position}
      toastOptions={{
        duration: toastConfig.duration,
        style: toastConfig.style,
        success: {
          style: {
            boxShadow: '0 0 20px rgba(16, 185, 129, 0.1)',
          },
          iconTheme: toastConfig.success.iconTheme,
        },
        error: {
          style: {
            boxShadow: '0 0 20px rgba(239, 68, 68, 0.1)',
          },
          iconTheme: toastConfig.error.iconTheme,
        },
        loading: {
          iconTheme: toastConfig.loading.iconTheme,
        },
      }}
    />
  );
};

// Utilitaires pour les notifications toast
export const toast = {
  success: (message: string, options?: ToastOptions) => {
    return hotToast.success(message, {
      ...options,
      style: {
        ...toastConfig.style,
        boxShadow: '0 0 25px rgba(16, 185, 129, 0.15)',
        ...options?.style,
      },
    });
  },

  error: (message: string, options?: ToastOptions) => {
    return hotToast.error(message, {
      duration: 6000, // Plus long pour les erreurs
      ...options,
      style: {
        ...toastConfig.style,
        boxShadow: '0 0 25px rgba(239, 68, 68, 0.15)',
        ...options?.style,
      },
    });
  },

  loading: (message: string, options?: ToastOptions) => {
    return hotToast.loading(message, {
      ...options,
      style: {
        ...toastConfig.style,
        boxShadow: '0 0 25px rgba(59, 130, 246, 0.15)',
        ...options?.style,
      },
    });
  },

  promise: <T,>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: ApiError) => string);
    },
    options?: ToastOptions
  ) => {
    return hotToast.promise(promise, messages, {
      ...options,
      style: toastConfig.style,
      success: {
        style: {
          ...toastConfig.style,
          boxShadow: '0 0 25px rgba(16, 185, 129, 0.15)',
        },
        iconTheme: toastConfig.success.iconTheme,
      },
      error: {
        style: {
          ...toastConfig.style,
          boxShadow: '0 0 25px rgba(239, 68, 68, 0.15)',
        },
        iconTheme: toastConfig.error.iconTheme,
      },
      loading: {
        style: {
          ...toastConfig.style,
          boxShadow: '0 0 25px rgba(59, 130, 246, 0.15)',
        },
        iconTheme: toastConfig.loading.iconTheme,
      },
    });
  },

  dismiss: (toastId?: string) => {
    return hotToast.dismiss(toastId);
  },

  remove: (toastId?: string) => {
    return hotToast.remove(toastId);
  },

  // Notifications spécialisées pour l'application médicale
  appointment: {
    created: () => toast.success(i18n.t('notifications.appointment.created')),
    updated: () => toast.success(i18n.t('notifications.appointment.updated')),
    cancelled: () => toast.success(i18n.t('notifications.appointment.cancelled')),
    confirmed: () => toast.success(i18n.t('notifications.appointment.confirmed')),
    reminder: (time: string) => toast.success(i18n.t('notifications.appointment.reminder', { time })),
  },

  teleconsult: {
    started: () => toast.success(i18n.t('notifications.teleconsult.started')),
    ended: () => toast.success(i18n.t('notifications.teleconsult.ended')),
    urgent: () => toast.error(i18n.t('notifications.teleconsult.urgent'), { duration: 8000 }),
    waitingRoom: () => toast.success(i18n.t('notifications.teleconsult.waitingRoom')),
  },

  auth: {
    loginSuccess: (name: string) => toast.success(i18n.t('notifications.auth.loginSuccess', { name })),
    loginError: () => toast.error(i18n.t('notifications.auth.loginError')),
    logout: () => toast.success(i18n.t('notifications.auth.logout')),
    sessionExpired: () => toast.error(i18n.t('notifications.auth.sessionExpired')),
    mfaRequired: () => toast.loading(i18n.t('notifications.auth.mfaRequired')),
    mfaSuccess: () => toast.success(i18n.t('notifications.auth.mfaSuccess')),
  },

  patient: {
    created: () => toast.success(i18n.t('notifications.patient.created')),
    updated: () => toast.success(i18n.t('notifications.patient.updated')),
    shortNumberGenerated: (number: string) => 
      toast.success(i18n.t('notifications.patient.shortNumberGenerated', { number }), { duration: 6000 }),
  },

  call: {
    incoming: (caller: string) => toast.success(i18n.t('notifications.call.incoming', { caller }), { duration: 8000 }),
    connected: () => toast.success(i18n.t('notifications.call.connected')),
    ended: () => toast.success(i18n.t('notifications.call.ended')),
    failed: () => toast.error(i18n.t('notifications.call.failed')),
  },

  network: {
    offline: () => toast.error(i18n.t('notifications.network.offline'), { duration: 0 }),
    online: () => toast.success(i18n.t('notifications.network.online')),
    slowConnection: () => toast.loading(i18n.t('notifications.network.slowConnection')),
  },
};

// Hook pour utiliser les toasts avec gestion d'état
export const useToast = () => {
  const showToast = (type: 'success' | 'error' | 'loading', message: string, options?: ToastOptions) => {
    return toast[type](message, options);
  };

  const showPromiseToast = <T,>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: ApiError) => string);
    }
  ) => {
    return toast.promise(promise, messages);
  };

  return {
    toast,
    showToast,
    showPromiseToast,
  };
};
